import Datepicker from 'components/ReusableComponents/Datepicker'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import useLocalStorage from 'hooks/useLocalStorageState'
import { hotelCampaignRegistrationStatuses } from 'main/hotelStatuses'
import { useEffect, useMemo, useState } from 'react'
import dashboardApi from 'services/api/dashboard'
import subsidiariesApi from 'services/api/subsidiaries'

import ChatView from './ChatView'
import ConversationList from './ConversationList'
import FilterPanel from './FilterPanel'
import StatsPanel from './StatsPanel'
import styles from './DailyConversations.module.scss'
import useDailyConversationsFilters from './useDailyConversationsFilters'

function getShortDate(date) {
  return date.toISOString().split('T')[0]
}

const DailyConversations = () => {
  const [conversations, setConversations] = useState()
  const [activeChatId, setActiveChatId] = useState()
  const [date, setDate] = useState(new Date(Date.now() - 86400000))
  const [hotels, setHotels] = useState([])

  const [filterState, dispatchFilter] = useDailyConversationsFilters()

  const {
    hotelNameSearch,
    selectedBotContexts,
    selectedBotContextsHasBeenModified,
    showUnreviewedOnly,
    botIntentFilter,
    isFilterPanelOpen,
    sortBy,
    hotelGroups,
    chains,
    brands,
    subsidiaryStatus,
    justShowFlagged,
    stayPeriodFilter
  } = filterState

  function addToSpamList(message) {
    const localStorageKey = 'dailyConversationsSpamList'
    let currentList = []

    if (localStorage.getItem(localStorageKey)) {
      currentList = JSON.parse(localStorage.getItem(localStorageKey))
    }

    localStorage.setItem(localStorageKey, JSON.stringify([...currentList, message]))
  }

  // Fetch hotels once on mount
  useEffect(() => {
    subsidiariesApi
      .getHotelList({ type: 'upAndRunning' })
      .then(hotelList => {
        setHotels(hotelList)
      })
      .catch(err => {
        console.error('Failed to fetch hotels:', err)
      })
  }, [])

  useEffect(() => {
    setActiveChatId(null)
    setConversations(null)
    dashboardApi.getDailyConversations(date.toISOString()).then(c => {
      setConversations(c)
    })
  }, [date])

  const [reviewedConversations, setReviewedConversations] = useLocalStorage(
    'reviewedConversations',
    {}
  )

  const [flaggedConversations, setFlaggedConversations] = useLocalStorage(
    'flaggedConversations',
    {}
  )

  // Create hotel lookup map by subsidiaryId
  const hotelMap = useMemo(() => {
    const map = new Map()
    hotels.forEach(h => {
      if (h.id) {
        map.set(h.id, h)
      }
    })
    return map
  }, [hotels])

  const enrichedConversations = useMemo(() => {
    if (!conversations) return []

    return conversations.map(c => {
      const hotel = hotelMap.get(c.subsidiaryId)
      return {
        ...c,
        groupName: hotel?.merchantName || null,
        chainCode: hotel?.chainCode || null,
        parentChainCode: hotel?.parentChainCode || null,
        subsidiaryStatus: hotel?.status || null
      }
    })
  }, [conversations, hotelMap])

  // Generate hotel groups and chains from enriched conversations
  const allHotelGroups = useMemo(() => {
    const groups = new Set()
    enrichedConversations.forEach(c => {
      if (c.groupName) {
        groups.add(c.groupName)
      }
    })
    const options = {}
    Array.from(groups)
      .sort()
      .forEach(group => {
        options[group] = group
      })
    return options
  }, [enrichedConversations])

  const allHotelChains = useMemo(() => {
    const chains = new Set()
    enrichedConversations.forEach(c => {
      if (c.chainCode) {
        chains.add(c.chainCode)
      }
    })
    const options = {}
    Array.from(chains)
      .sort()
      .forEach(chain => {
        options[chain] = chain
      })
    return options
  }, [enrichedConversations])

  const botContextOptions = useMemo(() => {
    if (!enrichedConversations) return {}

    const contexts = new Set()
    enrichedConversations.forEach(c => {
      if (c.botContext) {
        contexts.add(c.botContext)
      }
      if (c.messages) {
        c.messages.forEach(m => {
          if (m.botContext) {
            contexts.add(m.botContext)
          }
        })
      }
    })
    const options = {}
    Array.from(contexts)
      .sort()
      .forEach(ctx => {
        options[ctx] = ctx
      })
    return options
  }, [enrichedConversations])

  const filteredConversations = useMemo(() => {
    if (!enrichedConversations) return []

    let filtered = enrichedConversations.filter(c => {
      if (hotelNameSearch.trim()) {
        const searchLower = hotelNameSearch.toLowerCase().trim()
        const hotelNameMatch = c.hotelName?.toLowerCase().includes(searchLower)
        const messageContentMatch = c.messages?.some(m =>
          m.content?.toLowerCase().includes(searchLower)
        )
        if (!hotelNameMatch && !messageContentMatch) {
          return false
        }
      }

      if (hotelGroups !== 'all') {
        if (c.groupName !== hotelGroups) {
          return false
        }
      }

      if (chains !== 'all') {
        if (c.chainCode !== chains) {
          return false
        }
      }

      if (brands !== 'all') {
        if (c.parentChainCode !== brands) {
          return false
        }
      }

      if (subsidiaryStatus.length > 0) {
        if (!c.subsidiaryStatus) {
          return false
        }
        if (hotelCampaignRegistrationStatuses.includes(c.subsidiaryStatus)) {
          if (!subsidiaryStatus.includes('PendingCampaignBrandRegistration')) {
            return false
          }
        } else {
          if (!subsidiaryStatus.includes(c.subsidiaryStatus)) {
            return false
          }
        }
      } else {
        // When zeroed: show only items without subsidiary status
        if (c.subsidiaryStatus) {
          return false
        }
      }

      if (selectedBotContexts.length > 0) {
        const hasMatchingContext =
          selectedBotContexts.includes(c.botContext) ||
          (c.messages && c.messages.some(m => selectedBotContexts.includes(m.botContext)))
        if (!hasMatchingContext) {
          return false
        }
      } else if (selectedBotContextsHasBeenModified) {
        // When zeroed: show only items without bot context
        const hasBotContext =
          c.botContext ||
          (c.messages && c.messages.some(m => m.botContext))
        if (hasBotContext) {
          return false
        }
      }


      if (showUnreviewedOnly) {
        const isReviewed = reviewedConversations?.[getShortDate(date)]?.[c.chatId]
        if (isReviewed) {
          return false
        }
      }

      if (botIntentFilter === 'hasIntent') {
        const hasIntent =
          (c.messages && c.messages.some(m => m.botIntent)) || c.botContext
        if (!hasIntent) {
          return false
        }
      } else if (botIntentFilter === 'noIntent') {
        const hasIntent =
          (c.messages && c.messages.some(m => m.botIntent)) || c.botContext
        if (hasIntent) {
          return false
        }
      }

      if (justShowFlagged) {
        const isFlagged = flaggedConversations?.[getShortDate(date)]?.[c.chatId]
        if (!isFlagged) {
          return false
        }
      }

      // Filter by stay period based on kind field
      if (stayPeriodFilter.length > 0) {
        const kindMapping = {
          before: 'Sales',
          during: 'Service',
          after: 'Review'
        }
        const expectedKinds = stayPeriodFilter.map(period => kindMapping[period])
        const conversationKind = c.Kind || c.kind
        const conversationCategory = c.Category || c.category
        const isReviewConversation =
          conversationKind === 'Review' ||
          conversationCategory === 'CheckedOutReview' ||
          conversationCategory === 'OTACheckedOutReview'
        let mappedKind = conversationKind
        if (isReviewConversation) {
          mappedKind = 'Review'
        }
        if (mappedKind && !expectedKinds.includes(mappedKind)) {
          return false
        }
        if (!mappedKind && stayPeriodFilter.length > 0) {
          return false
        }
      } else {
        // When zeroed: show only items without stay period (no kind)
        const conversationKind = c.Kind || c.kind
        const conversationCategory = c.Category || c.category
        const isReviewConversation =
          conversationKind === 'Review' ||
          conversationCategory === 'CheckedOutReview' ||
          conversationCategory === 'OTACheckedOutReview'
        const hasKind = conversationKind || isReviewConversation
        if (hasKind) {
          return false
        }
      }

      return true
    })

    const sorted = [...filtered].sort((a, b) => {
      const aReviewed = reviewedConversations?.[getShortDate(date)]?.[a.chatId]
      const bReviewed = reviewedConversations?.[getShortDate(date)]?.[b.chatId]
      const aFlagged = flaggedConversations?.[getShortDate(date)]?.[a.chatId]
      const bFlagged = flaggedConversations?.[getShortDate(date)]?.[b.chatId]
      const aMessageCount = a.messages?.length || 0
      const bMessageCount = b.messages?.length || 0

      switch (sortBy) {
        case 'hotelNameAsc':
          return (a.hotelName || '').localeCompare(b.hotelName || '')
        case 'hotelNameDesc':
          return (b.hotelName || '').localeCompare(a.hotelName || '')
        case 'messageCountDesc':
          return bMessageCount - aMessageCount
        case 'messageCountAsc':
          return aMessageCount - bMessageCount
        case 'unreviewedFirst':
          if (aReviewed && !bReviewed) return 1
          if (!aReviewed && bReviewed) return -1
          return 0
        case 'flaggedFirst':
          if (aFlagged && !bFlagged) return -1
          if (!aFlagged && bFlagged) return 1
          return 0
        default:
          return 0
      }
    })

    return sorted
  }, [
    enrichedConversations,
    hotelNameSearch,
    hotelGroups,
    chains,
    brands,
    subsidiaryStatus,
    selectedBotContexts,
    selectedBotContextsHasBeenModified,
    showUnreviewedOnly,
    botIntentFilter,
    justShowFlagged,
    stayPeriodFilter,
    reviewedConversations,
    flaggedConversations,
    date,
    sortBy
  ])

  const statistics = useMemo(() => {
    if (!filteredConversations) {
      return {
        total: 0,
        unreviewed: 0,
        flagged: 0,
        withIntent: 0,
        noIntent: 0,
        avgMessages: 0
      }
    }

    const total = filteredConversations.length
    let unreviewed = 0
    let flagged = 0
    let withIntent = 0
    let noIntent = 0
    let totalMessages = 0

    filteredConversations.forEach(c => {
      const isReviewed = reviewedConversations?.[getShortDate(date)]?.[c.chatId]
      const isFlagged = flaggedConversations?.[getShortDate(date)]?.[c.chatId]
      const hasIntent =
        (c.messages && c.messages.some(m => m.botIntent)) || c.botContext

      if (!isReviewed) unreviewed++
      if (isFlagged) flagged++
      if (hasIntent) {
        withIntent++
      } else {
        noIntent++
      }
      totalMessages += c.messages?.length || 0
    })

    return {
      total,
      unreviewed,
      flagged,
      withIntent,
      noIntent,
      avgMessages: total > 0 ? Math.round((totalMessages / total) * 10) / 10 : 0
    }
  }, [filteredConversations, reviewedConversations, flaggedConversations, date])

  const activeChat = filteredConversations.find(c => c.chatId === activeChatId)

  const groupedConversationsByHotel = useMemo(() => {
    const groupedByHotel = filteredConversations.reduce((acc, c) => {
      const hotelName = c.hotelName || 'Unknown Hotel'
      if (!acc[hotelName]) {
        acc[hotelName] = []
      }
      acc[hotelName].push(c)
      return acc
    }, {})

    const sortedHotelNames = Object.keys(groupedByHotel).sort()

    return sortedHotelNames.map(hotelName => ({
      hotelName,
      conversations: groupedByHotel[hotelName]
    }))
  }, [filteredConversations])

  useEffect(() => {
    if (activeChatId && !activeChat) {
      setActiveChatId(null)
    }
  }, [activeChatId, activeChat])

  if (!conversations) return <LoadingIndicator />

  function handleChatRowClick(chatId) {
    setActiveChatId(chatId)
    setReviewedConversations({
      ...reviewedConversations,
      [getShortDate(date)]: {
        ...reviewedConversations[getShortDate(date)],
        [chatId]: true
      }
    })
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Daily conversations" isFixed width={1100}>
        <Datepicker onChange={setDate} selected={date} maxDate={new Date() - 1} />
      </CustomHeader>
      <div className={styles.contentWrapper}>
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onToggle={() =>
            dispatchFilter({
              type: 'isFilterPanelOpen',
              payload: !isFilterPanelOpen
            })
          }
          filterState={filterState}
          dispatchFilter={dispatchFilter}
          allHotelGroups={allHotelGroups}
          allHotelChains={allHotelChains}
          botContextOptions={botContextOptions}
        />
        <div className={styles.mainContent}>
          <div className={styles.sidebar}>
            <StatsPanel statistics={statistics} />
            {filteredConversations.length === 0 ? (
              <div className={styles.noResults}>No conversations match the filters</div>
            ) : (
              <>
                <div className={styles.resultsCount}>
                  Showing {filteredConversations.length} of {enrichedConversations.length}{' '}
                  conversations
                </div>
                <ConversationList
                  groupedConversationsByHotel={groupedConversationsByHotel}
                  activeChatId={activeChatId}
                  onChatRowClick={handleChatRowClick}
                  reviewedConversations={reviewedConversations}
                  flaggedConversations={flaggedConversations}
                  date={date}
                />
              </>
            )}
          </div>
          {activeChatId && activeChat && (
            <ChatView
              activeChat={activeChat}
              activeChatId={activeChatId}
              reviewedConversations={reviewedConversations}
              flaggedConversations={flaggedConversations}
              setReviewedConversations={setReviewedConversations}
              setFlaggedConversations={setFlaggedConversations}
              onAddToSpamList={addToSpamList}
              date={date}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default DailyConversations
